import { HHStack, HHTypography } from '@hinge-health/react-component-library';
import { LinearProgress } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  ADJUSTMENT_MAX_FILE_BYTES,
  BULK_ADJUSTMENTS_CATEGORY,
  getAdjustmentFileNameError,
} from './bulk-adjustments';
import FileUploadDropzone from './file-upload-dropzone';
import GSheetUrlInput from './gsheet-url-input';
import { UploadedFile } from './uploaded-file';

export const UPLOAD_METHOD_SUBHEADING_TEST_ID = 'upload-method-subheading';

export const ADJUSTMENT_UPLOAD_PROGRESS_TEST_ID =
  'csv-upload-adjustment-progress-bar';

interface UploadMethodSectionProps {
  isUploading: boolean;
  progress: number;
  headerError?: string;
}

export const UploadMethodSection = ({
  isUploading,
  progress,
  headerError,
}: UploadMethodSectionProps): JSX.Element => {
  const { control, setValue } = useFormContext();
  const uploadedFile = useWatch({ control, name: 'file' });
  const selectedFileCategory = useWatch({
    control,
    name: 'fileCategorySelect',
  });
  const fileNameError = getAdjustmentFileNameError(
    selectedFileCategory,
    uploadedFile,
  );
  const isBulkAdjustments = selectedFileCategory === BULK_ADJUSTMENTS_CATEGORY;

  return (
    <HHStack direction="column" spacing={2}>
      <HHTypography hhVariant="variant-bypass" variant="h6">
        Upload Method
      </HHTypography>
      {!isBulkAdjustments && (
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="text.secondary"
          data-testid={UPLOAD_METHOD_SUBHEADING_TEST_ID}
        >
          Choose one of the following upload methods
        </HHTypography>
      )}
      <Controller
        control={control}
        name="file"
        render={({
          field: { onChange, value, onBlur },
          fieldState: { error },
        }): JSX.Element => (
          <FileUploadDropzone
            // Remount so a size error from the old category's limit goes away.
            key={selectedFileCategory}
            value={value}
            onChange={(file): void => {
              onChange(file);
              if (file) {
                setValue('googleSheetsUrl', '');
              }
            }}
            onBlur={onBlur}
            error={error?.message ?? fileNameError ?? headerError}
            maxFileSize={
              isBulkAdjustments ? ADJUSTMENT_MAX_FILE_BYTES : undefined
            }
          />
        )}
      />
      {uploadedFile && (
        <HHStack direction="column" spacing={2}>
          <HHTypography hhVariant="variant-bypass" variant="h6">
            Selected File
          </HHTypography>
          {isUploading && (
            <LinearProgress
              variant="determinate"
              value={progress}
              aria-label="Adjustment file upload progress"
              aria-valuetext={`${progress} percent uploaded`}
              data-testid={ADJUSTMENT_UPLOAD_PROGRESS_TEST_ID}
            />
          )}
          <UploadedFile file={uploadedFile} />
        </HHStack>
      )}
      {!isBulkAdjustments && (
        <>
          <HHTypography
            hhVariant="variant-bypass"
            variant="body2"
            color="text.secondary"
            textAlign="center"
          >
            OR
          </HHTypography>
          <GSheetUrlInput />
        </>
      )}
    </HHStack>
  );
};
